import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
  Button,
  Input,
  Badge,
  Collapse,
} from 'reactstrap';
import { vehiclesAPI } from '../../helpers/api';
import { useIsAdmin } from '../../helpers/hooks';
import { useIsMobile } from '../../helpers/useMediaQuery';
import VehicleDetailModal from './VehicleDetailModal';
import VehicleFormModal from './VehicleFormModal';

interface Vehicle {
  id: string;
  plate: string;
  brand: string | null;
  model: string | null;
  type: string;
  isMachinery: boolean;
  ownerName: string | null;
  dailyRate: number | null;
  isActive: boolean;
  lastDriver: string | null;
}

const ownershipLabels: Record<string, string> = {
  COMPANY: 'Şirket',
  RENTAL: 'Kiralık',
};

const ownershipStyles: Record<string, string> = {
  COMPANY: 'bg-primary bg-opacity-10 text-primary',
  RENTAL: 'bg-info bg-opacity-10 text-info',
};

// Helper to get display label for vehicle type
const getVehicleTypeLabel = (vehicle: Vehicle) => {
  const ownership = ownershipLabels[vehicle.type] || vehicle.type;
  if (vehicle.isMachinery) {
    return `${ownership} İş Makinesi`;
  }
  return `${ownership} Araç`;
};

// Helper to get style for vehicle
const getVehicleTypeStyle = (vehicle: Vehicle) => {
  if (vehicle.isMachinery) {
    return 'bg-warning bg-opacity-10 text-warning';
  }
  return ownershipStyles[vehicle.type] || 'bg-secondary bg-opacity-10 text-secondary';
};

const VehicleList: React.FC = () => {
  const isAdmin = useIsAdmin();
  const isMobile = useIsMobile();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [formModalOpen, setFormModalOpen] = useState(false);

  // Filter states
  const [typeFilter, setTypeFilter] = useState(''); // COMPANY or RENTAL
  const [machineryFilter, setMachineryFilter] = useState(''); // '' | 'true' | 'false'
  const [statusFilter, setStatusFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Detail modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);

  const openDetailModal = (vehicle: Vehicle, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedVehicle(vehicle);
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedVehicle(null);
  };

  useEffect(() => {
    fetchVehicles();
  }, [typeFilter, machineryFilter, statusFilter, searchFilter]);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const params: { type?: string; isActive?: boolean; isMachinery?: boolean } = {};
      if (typeFilter) params.type = typeFilter;
      if (machineryFilter !== '') params.isMachinery = machineryFilter === 'true';
      if (statusFilter !== '') params.isActive = statusFilter === 'true';

      const response = await vehiclesAPI.getAll(params);
      let filtered = response.data;
      if (searchFilter) {
        const s = searchFilter.toLowerCase();
        filtered = filtered.filter((v: Vehicle) =>
          v.plate.toLowerCase().includes(s) ||
          (v.brand && v.brand.toLowerCase().includes(s)) ||
          (v.model && v.model.toLowerCase().includes(s)) ||
          (v.ownerName && v.ownerName.toLowerCase().includes(s)) ||
          (v.lastDriver && v.lastDriver.toLowerCase().includes(s))
        );
      }
      setVehicles(filtered);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setTypeFilter('');
    setMachineryFilter('');
    setStatusFilter('');
    setSearchFilter('');
  };

  const hasActiveFilters = typeFilter || machineryFilter || statusFilter || searchFilter;

  return (
    <>
      <Row>
        <Col xs={12}>
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">Araçlar</h4>
            <div className="page-title-right">
              {isAdmin && (
                <Button color="primary" onClick={() => setFormModalOpen(true)}>
                  <i className="ri-add-line me-1"></i>
                  Yeni Araç
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={3}>
                  <h5 className="card-title mb-0">Araç Listesi</h5>
                </Col>
                {/* Desktop Filters */}
                {!isMobile && (
                  <Col md={9}>
                    <Row className="g-2 align-items-center justify-content-end">
                      <Col md="auto">
                        <Input
                          type="text"
                          bsSize="sm"
                          value={searchFilter}
                          onChange={(e) => setSearchFilter(e.target.value)}
                          placeholder="Plaka, marka, model..."
                          style={{ width: '170px' }}
                        />
                      </Col>
                      <Col md="auto">
                        <Input
                          type="select"
                          bsSize="sm"
                          value={typeFilter}
                          onChange={(e) => setTypeFilter(e.target.value)}
                          style={{ minWidth: '130px' }}
                        >
                          <option value="">Tüm Sahiplik</option>
                          <option value="COMPANY">Şirket</option>
                          <option value="RENTAL">Kiralık</option>
                        </Input>
                      </Col>
                      <Col md="auto">
                        <Input
                          type="select"
                          bsSize="sm"
                          value={machineryFilter}
                          onChange={(e) => setMachineryFilter(e.target.value)}
                          style={{ minWidth: '130px' }}
                        >
                          <option value="">Tüm Kategoriler</option>
                          <option value="false">Araçlar</option>
                          <option value="true">İş Makineleri</option>
                        </Input>
                      </Col>
                      <Col md="auto">
                        <Input
                          type="select"
                          bsSize="sm"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          style={{ minWidth: '120px' }}
                        >
                          <option value="">Tüm Durumlar</option>
                          <option value="true">Aktif</option>
                          <option value="false">Pasif</option>
                        </Input>
                      </Col>
                      {hasActiveFilters && (
                        <Col md="auto">
                          <Button
                            color="light"
                            size="sm"
                            onClick={clearFilters}
                            title="Filtreleri Temizle"
                          >
                            <i className="ri-close-line"></i>
                          </Button>
                        </Col>
                      )}
                    </Row>
                  </Col>
                )}
              </Row>
            </CardHeader>
            <CardBody>
              {/* Mobile Filters */}
              {isMobile && (
                <>
                  <button
                    className="btn btn-sm btn-light w-100 mb-3 d-flex align-items-center justify-content-between"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <span>
                      <i className="ri-filter-3-line me-2"></i>
                      Filtreler
                      {hasActiveFilters && (
                        <Badge color="primary" className="ms-2">
                          {[typeFilter, machineryFilter, statusFilter].filter(Boolean).length}
                        </Badge>
                      )}
                    </span>
                    <i className={`ri-arrow-${showFilters ? 'up' : 'down'}-s-line`}></i>
                  </button>

                  <Collapse isOpen={showFilters}>
                    <div className="bg-light p-3 rounded mb-3">
                      <div className="mb-2">
                        <label className="small text-muted mb-1">Ara</label>
                        <Input
                          type="text"
                          bsSize="sm"
                          value={searchFilter}
                          onChange={(e) => setSearchFilter(e.target.value)}
                          placeholder="Plaka, marka, model..."
                        />
                      </div>

                      <div className="mb-2">
                        <label className="small text-muted mb-1">Sahiplik</label>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={typeFilter}
                          onChange={(e) => setTypeFilter(e.target.value)}
                        >
                          <option value="">Tüm Sahiplik</option>
                          <option value="COMPANY">Şirket</option>
                          <option value="RENTAL">Kiralık</option>
                        </Input>
                      </div>

                      <div className="mb-2">
                        <label className="small text-muted mb-1">Kategori</label>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={machineryFilter}
                          onChange={(e) => setMachineryFilter(e.target.value)}
                        >
                          <option value="">Tüm Kategoriler</option>
                          <option value="false">Araçlar</option>
                          <option value="true">İş Makineleri</option>
                        </Input>
                      </div>

                      <div className="mb-2">
                        <label className="small text-muted mb-1">Durum</label>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <option value="">Tüm Durumlar</option>
                          <option value="true">Aktif</option>
                          <option value="false">Pasif</option>
                        </Input>
                      </div>

                      {hasActiveFilters && (
                        <button
                          className="btn btn-sm btn-outline-secondary w-100 mt-2"
                          onClick={clearFilters}
                        >
                          <i className="ri-close-line me-1"></i>
                          Filtreleri Temizle
                        </button>
                      )}
                    </div>
                  </Collapse>
                </>
              )}

              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                  </div>
                </div>
              ) : vehicles.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">Henüz araç kaydı bulunmuyor.</p>
                </div>
              ) : isMobile ? (
                // Mobile Card View
                <div className="admin-card-list">
                  {vehicles.map((vehicle) => (
                    <div key={vehicle.id} className="admin-card-item">
                      {/* Header: Plate + Type */}
                      <div
                        className="admin-card-header"
                        onClick={() => openDetailModal(vehicle)}
                        style={{ cursor: 'pointer' }}
                      >
                        <h6 className="admin-card-title">{vehicle.plate}</h6>
                        <span
                          className={`px-2 py-1 rounded-pill small ${getVehicleTypeStyle(vehicle)}`}
                          style={{ fontWeight: 500 }}
                        >
                          {getVehicleTypeLabel(vehicle)}
                        </span>
                      </div>

                      {/* Body: Details */}
                      <div
                        className="admin-card-body"
                        onClick={() => openDetailModal(vehicle)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-building-line"></i>Marka
                          </span>
                          <span className="admin-info-value">
                            {vehicle.brand || '-'}
                          </span>
                        </div>
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-car-line"></i>Model
                          </span>
                          <span className="admin-info-value">
                            {vehicle.model || '-'}
                          </span>
                        </div>
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-user-line"></i>Son Kullanıcı
                          </span>
                          <span className="admin-info-value">{vehicle.lastDriver || '-'}</span>
                        </div>
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-checkbox-circle-line"></i>Durum
                          </span>
                          <span className="admin-info-value">
                            <span
                              className={`px-2 py-1 rounded-pill small ${
                                vehicle.isActive
                                  ? 'bg-success bg-opacity-10 text-success'
                                  : 'bg-secondary bg-opacity-10 text-secondary'
                              }`}
                              style={{ fontWeight: 500 }}
                            >
                              {vehicle.isActive ? 'Aktif' : 'Pasif'}
                            </span>
                          </span>
                        </div>
                      </div>

                      {/* Footer: Actions */}
                      <div className="admin-card-footer">
                        <button
                          type="button"
                          className="admin-action-btn admin-btn-primary"
                          style={{ flex: 1 }}
                          onClick={(e) => openDetailModal(vehicle, e)}
                        >
                          <i className="ri-eye-line"></i>
                          Detaylar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Desktop Table View
                <div className="table-responsive">
                  <Table className="table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Plaka</th>
                        <th>Marka</th>
                        <th>Model</th>
                        <th>Tür</th>
                        <th>Son Kullanıcı</th>
                        <th>Durum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vehicles.map((vehicle) => (
                        <tr
                          key={vehicle.id}
                          onClick={() => openDetailModal(vehicle)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>
                            <strong>{vehicle.plate}</strong>
                          </td>
                          <td>{vehicle.brand || '-'}</td>
                          <td>{vehicle.model || '-'}</td>
                          <td>
                            <span
                              className={`px-2 py-1 rounded-pill small ${getVehicleTypeStyle(vehicle)}`}
                              style={{ fontWeight: 500 }}
                            >
                              {getVehicleTypeLabel(vehicle)}
                            </span>
                          </td>
                          <td>{vehicle.lastDriver || '-'}</td>
                          <td>
                            <span
                              className={`px-2 py-1 rounded-pill small ${
                                vehicle.isActive
                                  ? 'bg-success bg-opacity-10 text-success'
                                  : 'bg-secondary bg-opacity-10 text-secondary'
                              }`}
                              style={{ fontWeight: 500 }}
                            >
                              {vehicle.isActive ? 'Aktif' : 'Pasif'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Yeni Araç Modal */}
      <VehicleFormModal
        isOpen={formModalOpen}
        toggle={() => setFormModalOpen(false)}
        onSuccess={fetchVehicles}
      />

      {/* Araç Detay Modal */}
      <VehicleDetailModal
        isOpen={detailModalOpen}
        toggle={closeDetailModal}
        vehicle={selectedVehicle}
        onUpdate={fetchVehicles}
      />
    </>
  );
};

export default VehicleList;
